import React from "react";

import { Grid } from "@material-ui/core";
import { useFormikContext } from "formik";
import { useSelector } from "react-redux";

import { SecondaryButton } from "components/Form";
import { withNamespace } from "components/Form/FormikControls/lib";
import { Row } from "components/Layout";
import { BuyerAwareBuyerWayField } from "components/Form/Fields";
import { FormikAwareBusinessPICSelector } from "components/Form/FormikControls/BusinessPICSelector";
import InlineConsignmentSelect from "components/SaleLotForms/InlineConsignmentSelect";

import { BUYER } from "constants/businesses";
import { ModalTypes } from "constants/navigation";

import { openModalLink } from "lib/navigation";

import {
  getConsignmentById,
  getCurrentSaleyardId,
  getDeploymentIdBySaleLotId,
} from "selectors";
import {
  useGetBestMatchingBuyerSplit,
  useGetBestMatchingVendorSplit,
} from "hooks/useGetBestMatchingVendorSplit";
import {
  useHasBuyerSplitPermission,
  useHasVendorSplitPermission,
  useToggle,
} from "hooks";

import {
  AddBuyerSplitDialog,
  PendingBuyerSplit,
} from "components/MobileTransactionalModal/Splits/AddBuyerSplitDialog";
import {
  AddVendorSplitDialog,
  PendingVendorSplit,
} from "components/MobileTransactionalModal/Splits/AddVendorSplitDialog";
import { SplitAllocationRow } from "components/MobileTransactionalModal/Splits/SplitAllocationRow";
import { TransactionalSaleFormValues } from "components/MobileTransactionalModal/lib/types";

interface WhoTabProps {
  saleLotNamespace: string;
  vendorSplitNamespace?: string;
  buyerSplitNamespace?: string;
  saleLotId?: string;
  onConsignmentChange?: (consignmentId: string) => void;
}

export const WhoTab = ({
  saleLotNamespace,
  vendorSplitNamespace,
  buyerSplitNamespace,
  saleLotId,
  onConsignmentChange,
}: WhoTabProps) => {
  const { values, setFieldValue } =
    useFormikContext<TransactionalSaleFormValues>();

  const {
    saleLot: { consignment_id: consignmentId, buyer_id: buyerId, quantity },
    buyerSplit,
    vendorSplit,
  } = values;

  const [isVendorSplitDialogOpen, toggleIsVendorSplitDialogOpen] =
    useToggle(false);
  const [isBuyerSplitDialogOpen, toggleIsBuyerSplitDialogOpen] =
    useToggle(false);

  const handleSaveVendorSplit = (split: PendingVendorSplit) => {
    if (vendorSplitNamespace) {
      setFieldValue(vendorSplitNamespace, split);
    }
  };
  const handleSaveBuyerSplit = (split: PendingBuyerSplit) => {
    if (buyerSplitNamespace) {
      setFieldValue(buyerSplitNamespace, split);
    }
  };

  const hasBuyerSplitPermission = useHasBuyerSplitPermission();
  const hasVendorSplitPermission = useHasVendorSplitPermission();

  const saleyardId = useSelector(getCurrentSaleyardId);

  const isCreatingSaleLotWithVendorSplitValuesInFormik =
    !saleLotId && vendorSplitNamespace;

  const isEditingSaleLotWithVendorSplitPermission =
    !!saleLotId && hasVendorSplitPermission;

  const isCreatingSaleLotWithBuyerSplitValuesInFormik =
    !saleLotId && buyerSplitNamespace;

  const isEditingSaleLotWithBuyerSplitPermission =
    !!saleLotId && hasBuyerSplitPermission;

  const consignment = useSelector(getConsignmentById(consignmentId || null));
  const vendorId = consignment?.vendor_id || null;

  const deploymentId: number | null | undefined = useSelector(
    getDeploymentIdBySaleLotId(saleLotId),
  );

  const bestMatchingVendorSplit = useGetBestMatchingVendorSplit({
    primaryBusinessId: vendorId,
    deploymentId,
    consignmentId: consignmentId || undefined,
    saleLotId,
  });
  const bestMatchingBuyerSplit = useGetBestMatchingBuyerSplit({
    primaryBusinessId: buyerId,
    deploymentId,
    saleLotId,
  });

  let displayVendorSplit: PendingVendorSplit | VendorSplit | null =
    vendorSplit || null;
  if (!displayVendorSplit && isEditingSaleLotWithVendorSplitPermission) {
    displayVendorSplit = bestMatchingVendorSplit || null;
  }

  let displayBuyerSplit: PendingBuyerSplit | BuyerSplit | null =
    buyerSplit ?? null;
  if (!displayBuyerSplit && isEditingSaleLotWithBuyerSplitPermission) {
    displayBuyerSplit = bestMatchingBuyerSplit || null;
  }

  const handleClickAddVendorSplit = () => {
    if (isCreatingSaleLotWithVendorSplitValuesInFormik) {
      toggleIsVendorSplitDialogOpen();
      return;
    } else if (
      isEditingSaleLotWithVendorSplitPermission &&
      vendorId &&
      deploymentId
    ) {
      openModalLink(ModalTypes.VendorSplit, {
        deploymentId,
        primaryBusinessId: vendorId,
        saleLotId,
      });
    }
  };

  const handleClickAddBuyerSplit = () => {
    if (isCreatingSaleLotWithBuyerSplitValuesInFormik) {
      toggleIsBuyerSplitDialogOpen();
      return;
    } else if (isEditingSaleLotWithBuyerSplitPermission && buyerId) {
      openModalLink(ModalTypes.BuyerSplit, {
        primaryBusinessId: buyerId,
        saleLotId,
      });
    }
  };

  return (
    <Grid item container xs={12}>
      <Row fullWidth marginVertical={2}>
        <InlineConsignmentSelect
          name={withNamespace(saleLotNamespace, "consignment_id")}
          label="Vendor"
          onChangeExtra={onConsignmentChange}
          showConsignmentCode={false}
          showAgencyName={false}
        />
      </Row>

      {hasVendorSplitPermission &&
        displayVendorSplit?.splits.map((s, i) => (
          <Row fullWidth key={i}>
            <SplitAllocationRow
              businessId={s.businessId}
              amount={s.amount}
              type={displayVendorSplit.type}
              gstOverride={
                "shouldChargeGSTOverride" in s
                  ? s.shouldChargeGSTOverride
                  : null
              }
            />
          </Row>
        ))}

      {hasVendorSplitPermission &&
        (isCreatingSaleLotWithVendorSplitValuesInFormik ||
          isEditingSaleLotWithVendorSplitPermission) && (
          <Row fullWidth marginVertical={2} flex justifyEnd>
            <Grid item xs={6} container spacing={2}>
              <SecondaryButton
                disabled={
                  isCreatingSaleLotWithVendorSplitValuesInFormik
                    ? !consignmentId
                    : !vendorId || !deploymentId
                }
                style={{ width: "100%" }}
                data-tour="add-vendor-split"
                type="button"
                onClick={handleClickAddVendorSplit}
              >
                {displayVendorSplit?.splits.length ? "Edit" : "Add"} Split
              </SecondaryButton>
            </Grid>
          </Row>
        )}

      <Row fullWidth marginVertical={2}>
        <FormikAwareBusinessPICSelector
          label="Buyer"
          subLabel="Search by Business Name or PIC"
          businessFieldName={withNamespace(saleLotNamespace, "buyer_id")}
          propertyFieldName={withNamespace(
            saleLotNamespace,
            "destination_property_id",
          )}
          businessRoles={[BUYER]}
          labelFontSize="charlie"
          subLabelFontSize="beta"
        />
      </Row>

      {hasBuyerSplitPermission &&
        displayBuyerSplit?.splits.map((s, i) => (
          <Row fullWidth key={i}>
            <SplitAllocationRow
              businessId={s.businessId}
              amount={s.amount}
              type={displayBuyerSplit.type}
            />
          </Row>
        ))}

      {hasBuyerSplitPermission &&
        (isCreatingSaleLotWithBuyerSplitValuesInFormik ||
          isEditingSaleLotWithBuyerSplitPermission) && (
          <Row fullWidth marginVertical={2} flex justifyEnd>
            <Grid item xs={6} container spacing={2}>
              <SecondaryButton
                disabled={
                  isCreatingSaleLotWithBuyerSplitValuesInFormik
                    ? !buyerId
                    : !buyerId || !deploymentId
                }
                style={{ width: "100%" }}
                data-tour="add-buyer-split"
                type="button"
                onClick={handleClickAddBuyerSplit}
              >
                {displayBuyerSplit?.splits.length ? "Edit" : "Add"} Split
              </SecondaryButton>
            </Grid>
          </Row>
        )}

      <Row fullWidth marginVertical={2}>
        <BuyerAwareBuyerWayField
          name={withNamespace(saleLotNamespace, "buyer_way")}
          label="Buyer Way"
          buyerField={withNamespace(saleLotNamespace, "buyer_id")}
          saleyardId={saleyardId}
        />
      </Row>

      {isVendorSplitDialogOpen && consignmentId && (
        <AddVendorSplitDialog
          primaryBusinessId={vendorId}
          quantity={quantity}
          initialSplit={vendorSplit}
          onSave={handleSaveVendorSplit}
          onClose={toggleIsVendorSplitDialogOpen}
        />
      )}

      {isBuyerSplitDialogOpen && buyerId && (
        <AddBuyerSplitDialog
          primaryBusinessId={buyerId}
          quantity={quantity}
          initialSplit={buyerSplit}
          onSave={handleSaveBuyerSplit}
          onClose={toggleIsBuyerSplitDialogOpen}
        />
      )}
    </Grid>
  );
};
